// src/pages/business/posts/BusinessPostDetail.tsx
// Web adaptation of mobile BusinessPostDetailScreen - View individual post details

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Heart, MessageCircle, Share2, MoreVertical,
  Trash2, Edit, Send, User, Calendar, Image, Loader2,
  Flag, Bookmark
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';
import { getImageUrl } from '@/config/media';

interface Comment {
  id: string;
  user_id: string;
  user_name: string;
  user_avatar?: string;
  content: string;
  created_at: string;
}

interface BusinessPost {
  id: string;
  business_id: string;
  content: string;
  media_urls?: string[];
  likes_count: number;
  comments_count: number;
  shares_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  business_name?: string;
  business_logo?: string;
  comments?: Comment[];
}

const BusinessPostDetail: React.FC = () => {
  const { businessId, postId } = useParams<{ businessId: string; postId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [post, setPost] = useState<BusinessPost | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(false);
  const [commenting, setCommenting] = useState(false);

  useEffect(() => {
    fetchPostDetails();
  }, [businessId, postId]);

  const fetchPostDetails = async () => {
    try {
      setLoading(true);
      const [postRes, commentsRes] = await Promise.all([
        apiClient.get(`/businesses/${businessId}/posts/${postId}`),
        apiClient.get(`/businesses/${businessId}/posts/${postId}/comments`)
      ]);
      setPost(postRes.data);
      setComments(commentsRes.data.comments || []);
    } catch (error: any) {
      console.error('Error fetching post details:', error);
      toastError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    try {
      await apiClient.post(`/businesses/${businessId}/posts/${postId}/like`);
      setLiked(!liked);
      if (post) {
        setPost({
          ...post,
          likes_count: liked ? post.likes_count - 1 : post.likes_count + 1
        });
      }
    } catch (error: any) {
      toastError('Failed to like post');
    }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;

    setCommenting(true);
    try {
      await apiClient.post(`/businesses/${businessId}/posts/${postId}/comments`, {
        content: newComment.trim()
      });
      toastSuccess('Comment added');
      setNewComment('');
      fetchPostDetails();
    } catch (error: any) {
      toastError('Failed to add comment');
    } finally {
      setCommenting(false);
    }
  };

  const handleShare = async () => {
    try {
      await apiClient.post(`/businesses/${businessId}/posts/${postId}/share`);
      toastSuccess('Post shared');
      if (post) {
        setPost({
          ...post,
          shares_count: post.shares_count + 1
        });
      }
    } catch (error: any) {
      toastError('Failed to share post');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}/posts`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Post Details
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <Loader2 size={40} className="animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <Image size={64} className="mx-auto mb-4 text-light-subtle dark:text-dark-subtle" />
          <p className="text-light-text dark:text-dark-text font-semibold">Post not found</p>
          <button
            onClick={() => navigate(`/business/${businessId}/posts`)}
            className="mt-4 px-6 py-2 rounded-xl text-white font-semibold"
            style={{ backgroundColor: '#4F46E5' }}
          >
            Back to Posts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}/posts`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Post Details
        </h1>
        <div className="flex items-center gap-2">
          <button className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
            <Edit size={18} className="text-light-subtle dark:text-dark-subtle" />
          </button>
          <button className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
            <MoreVertical size={18} className="text-light-subtle dark:text-dark-subtle" />
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Post Content */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
          {/* Author Info */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User size={24} style={{ color: '#4F46E5' }} />
            </div>
            <div className="flex-1">
              <p className="font-bold text-light-text dark:text-dark-text">
                {post.business_name || 'Your Business'}
              </p>
              <div className="flex items-center gap-2">
                <p className="text-xs text-light-subtle dark:text-dark-subtle">
                  {formatDate(post.created_at)}
                </p>
                <span
                  className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    post.is_active
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {post.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Post Text */}
          <p className="text-sm text-light-text dark:text-dark-text mb-4 whitespace-pre-wrap">
            {post.content}
          </p>

          {/* Post Media */}
          {post.media_urls && post.media_urls.length > 0 && (
            <div className="mb-4 rounded-lg overflow-hidden">
              <img
                src={getImageUrl(post.media_urls[0])}
                alt="Post media"
                className="w-full h-64 object-cover"
              />
            </div>
          )}

          {/* Post Stats */}
          <div className="flex items-center gap-4 pt-4 border-t border-light-border dark:border-dark-border">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                liked
                  ? 'bg-red-50 dark:bg-red-900/20 text-red-500'
                  : 'bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle hover:bg-red-50 dark:hover:bg-red-900/20'
              }`}
            >
              <Heart size={16} fill={liked ? 'currentColor' : 'none'} />
              <span className="text-sm font-medium">{post.likes_count}</span>
            </button>
            <div className="flex items-center gap-2 text-light-subtle dark:text-dark-subtle">
              <MessageCircle size={16} />
              <span className="text-sm">{post.comments_count}</span>
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <Share2 size={16} />
              <span className="text-sm">{post.shares_count}</span>
            </button>
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
          <h3 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">
            Comments ({comments.length})
          </h3>

          {/* Add Comment */}
          <div className="flex items-start gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center">
              <User size={20} style={{ color: '#4F46E5' }} />
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleComment}
                  disabled={commenting || !newComment.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#4F46E5' }}
                >
                  {commenting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      Post Comment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Comments List */}
          {comments.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle size={48} className="mx-auto mb-3 text-light-subtle dark:text-dark-subtle" />
              <p className="text-sm text-light-subtle dark:text-dark-subtle">No comments yet</p>
              <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">Be the first to comment</p>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.id} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-light-bg dark:bg-dark-bg flex-shrink-0 flex items-center justify-center">
                    <User size={18} className="text-light-subtle dark:text-dark-subtle" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm text-light-text dark:text-dark-text">
                        {comment.user_name}
                      </p>
                      <p className="text-xs text-light-subtle dark:text-dark-subtle">
                        {formatDate(comment.created_at)}
                      </p>
                    </div>
                    <p className="text-sm text-light-text dark:text-dark-text mt-1">
                      {comment.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessPostDetail;